// ===========================================
// Reset Password Page
// ===========================================

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useMatch, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Link,
  LinearProgress,
} from '@mui/material';
import {
  Lock,
  Visibility,
  VisibilityOff,
  ArrowBack,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { verifyResetToken, resetPassword } from '../api/auth.api';
import { AxiosError } from 'axios';
import { useTenant } from '../contexts/TenantContext';

// Password strength calculation
function getPasswordStrength(password: string): {
  score: number;
  label: string;
  color: 'error' | 'warning' | 'info' | 'success';
} {
  let score = 0;

  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;

  if (score <= 2) return { score: (score / 6) * 100, label: 'Weak', color: 'error' };
  if (score <= 4) return { score: (score / 6) * 100, label: 'Fair', color: 'warning' };
  if (score <= 5) return { score: (score / 6) * 100, label: 'Good', color: 'info' };
  return { score: 100, label: 'Strong', color: 'success' };
}

export default function ResetPasswordPage() {
  const { token, schoolSlug } = useParams<{ token: string; schoolSlug: string }>();
  const navigate = useNavigate();
  const { school } = useTenant();
  // The setup email links to /:schoolSlug/set-password/:token. An invalid token tells the page
  // nothing, so the route decides the copy then; a valid token's server `purpose` decides otherwise.
  const setupRoute = useMatch('/:schoolSlug/set-password/:token') !== null;
  const tenantName = school?.name?.trim();
  const schoolName = tenantName || 'your school';

  const [isVerifying, setIsVerifying] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [purpose, setPurpose] = useState<'RESET' | 'ACCOUNT_SETUP' | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const isSetup = (purpose ?? (setupRoute ? 'ACCOUNT_SETUP' : 'RESET')) === 'ACCOUNT_SETUP';

  const passwordStrength = getPasswordStrength(password);
  const passwordsMatch = password === confirmPassword;
  const isFormValid =
    password.length >= 8 &&
    passwordsMatch &&
    passwordStrength.score >= 50;

  // Verify token on mount
  useEffect(() => {
    async function checkToken() {
      if (!token) {
        setIsVerifying(false);
        setIsTokenValid(false);
        return;
      }

      try {
        const response = await verifyResetToken(token);
        setIsTokenValid(response.data.valid);
        // An older server sends no purpose: the route decides then, as for an invalid token (plan §3.9).
        setPurpose(response.data.valid ? (response.data.purpose ?? null) : null);
      } catch {
        setIsTokenValid(false);
      } finally {
        setIsVerifying(false);
      }
    }

    checkToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !isFormValid) return;

    setError('');
    setIsSubmitting(true);

    try {
      await resetPassword({ token, newPassword: password });
      setSuccess(true);
    } catch (err) {
      const axiosError = err as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message ||
        'Something went wrong. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isVerifying) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          {/* Logo/Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
                color: 'primary.main',
                mb: 1,
              }}
            >
              Music 'n Me
            </Typography>
          </Box>

          {/* Invalid Token State */}
          {!isTokenValid && !success && (
            <Box sx={{ textAlign: 'center' }}>
              <ErrorIcon
                sx={{
                  fontSize: 64,
                  color: 'error.main',
                  mb: 2,
                }}
              />
              <Typography variant="h6" color="text.primary" sx={{ mb: 2 }}>
                Invalid or Expired Link
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {isSetup
                  ? `This link has expired or has already been used. Ask ${schoolName} to send you a new one, or use Forgot password with your email.`
                  : 'This password reset link is invalid or has expired. Please request a new one.'}
              </Typography>
              <Button
                component={RouterLink}
                to={`/${schoolSlug}/forgot-password`}
                variant="contained"
                fullWidth
                sx={{ mb: 2 }}
              >
                {isSetup ? 'Forgot password' : 'Request New Link'}
              </Button>
              <Link
                component={RouterLink}
                to={`/${schoolSlug}/login`}
                underline="hover"
                color="text.secondary"
              >
                Back to Login
              </Link>
            </Box>
          )}

          {/* Success State */}
          {success && (
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircle
                sx={{
                  fontSize: 64,
                  color: 'success.main',
                  mb: 2,
                }}
              />
              <Typography variant="h6" color="text.primary" sx={{ mb: 2 }}>
                {isSetup ? 'Your password is set' : 'Password Reset Successful'}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {isSetup
                  ? 'You can now log in.'
                  : 'Your password has been reset. You can now log in with your new password.'}
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate(`/${schoolSlug}/login`)}
              >
                Go to Login
              </Button>
            </Box>
          )}

          {/* Reset Password Form */}
          {isTokenValid && !success && (
            <>
              <Typography variant="h5" color="text.primary" align="center" sx={{ mb: 1 }}>
                {isSetup ? 'Set your password' : 'Reset Your Password'}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ mb: 3 }}
              >
                {isSetup
                  ? `${tenantName ? `Welcome to ${tenantName}. ` : ''}Choose a password for your account.`
                  : 'Enter your new password below.'}
              </Typography>

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  autoFocus
                  sx={{ mb: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Password Strength Indicator */}
                {password && (
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={passwordStrength.score}
                      color={passwordStrength.color}
                      sx={{ height: 6, borderRadius: 1, mb: 0.5 }}
                    />
                    <Typography
                      variant="caption"
                      color={`${passwordStrength.color}.main`}
                    >
                      Password strength: {passwordStrength.label}
                    </Typography>
                  </Box>
                )}

                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  error={confirmPassword.length > 0 && !passwordsMatch}
                  helperText={
                    confirmPassword.length > 0 && !passwordsMatch
                      ? 'Passwords do not match'
                      : ''
                  }
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          aria-label={
                            showConfirmPassword ? 'Hide password' : 'Show password'
                          }
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Password Requirements */}
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    Password must be at least 8 characters and include uppercase, lowercase, number, and special character.
                  </Typography>
                </Alert>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isSubmitting || !isFormValid}
                  sx={{
                    mb: 2,
                    py: 1.5,
                    fontSize: '1rem',
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    isSetup ? 'Set Password' : 'Reset Password'
                  )}
                </Button>
              </form>

              {/* Back to Login Link */}
              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component={RouterLink}
                  to={`/${schoolSlug}/login`}
                  underline="hover"
                  color="text.secondary"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                  <ArrowBack fontSize="small" />
                  Back to Login
                </Link>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
